// A lap around the park lawn. The gate sits between two crates on the south
// side of the open grass. Cross it to start, touch the three marks in order,
// then cross the gate again to finish.

export type Vec = { x: number; z: number };
export type Gate = { x: number; z: number; half: number };
export type Course = {
  gate: Gate;
  marks: Vec[];
  radius: number;
  crates: { model: string; x: number; z: number }[];
};

export type Lap = {
  phase: "idle" | "run";
  /** How many marks have been touched. Equal to marks.length means return to the gate. */
  mark: number;
  startedAt: number;
  best: number | null;
  flashUntil: number;
  last: number | null;
};

export function freshLap(): Lap {
  return { phase: "idle", mark: 0, startedAt: 0, best: null, flashUntil: 0, last: null };
}

/** Gate and marks for the park at `park`. Crates flank the gate and stay out of the meeting square. */
export function parkCourse(park: Vec): Course {
  const gateZ = park.z + 6;
  return {
    gate: { x: park.x, z: gateZ, half: 1.5 },
    marks: [
      { x: park.x + 8, z: park.z },
      { x: park.x, z: park.z - 8 },
      { x: park.x - 8, z: park.z },
    ],
    radius: 3.5,
    crates: [
      { model: "kaykit-city/box_A.gltf", x: park.x - 2.6, z: gateZ },
      { model: "kaykit-city/box_B.gltf", x: park.x + 2.6, z: gateZ },
    ],
  };
}

/** True when the step from `prev` to `now` crosses the gate line inside its width. */
export function crossedGate(prev: Vec, now: Vec, gate: Gate): boolean {
  const dz0 = prev.z - gate.z;
  const dz1 = now.z - gate.z;
  if (dz0 * dz1 > 0) return false;
  if (now.z === prev.z) return dz0 === 0 && Math.abs(now.x - gate.x) <= gate.half;
  const t = (gate.z - prev.z) / (now.z - prev.z);
  if (t < 0 || t > 1) return false;
  const x = prev.x + (now.x - prev.x) * t;
  return Math.abs(x - gate.x) <= gate.half;
}

/** Advance a lap. A gate cross starts a run, or finishes one after every mark. */
export function stepLap(lap: Lap, prev: Vec, now: Vec, course: Course, time: number): Lap {
  const next: Lap = { ...lap };
  const gate = crossedGate(prev, now, course.gate);
  if (next.phase === "run" && next.mark >= course.marks.length && gate) {
    const last = Math.max(0, time - next.startedAt);
    next.last = last;
    next.best = next.best == null ? last : Math.min(next.best, last);
    next.flashUntil = time + 5;
    next.phase = "idle";
    next.mark = 0;
    return next;
  }
  if (next.phase === "idle" && gate) {
    next.phase = "run";
    next.mark = 0;
    next.startedAt = time;
    return next;
  }
  if (next.phase === "run" && next.mark < course.marks.length) {
    const m = course.marks[next.mark];
    if (Math.hypot(now.x - m.x, now.z - m.z) <= course.radius) next.mark += 1;
  }
  return next;
}
